import { dateService, FORMATS } from '@/global/services/date'
import {
  COVERAGE_PERCENT_DECIMALS,
  KILOMETRE_DECIMALS,
  METRES_IN_KILOMETRE,
  MINUTES_IN_HOUR,
  SECONDS_IN_MINUTE,
  WORK_ORDER_KIND,
  WORK_ORDERS_OVERVIEW_TRANSLATION_NAMESPACE as NS
} from '@/modules/road-maintenance-module/winter-service-v2/work-orders/overview/config/constants'

/**
 * The backend reports a work order nested - the vehicle, the driver, the task, the
 * status and the contractor side each as their own object, the progress under
 * `coverage` and `locations` - while the table columns and the details fields each
 * show one flat value. This is the single place that translation lives, so the
 * listing and the details screen can never read an order differently.
 *
 * Nothing here writes: work orders are not edited, only looked at.
 */

/**
 * A timestamp as the screens show it. The backend sends `Y-m-d H:i:s`, which is not
 * what a user reads.
 */
export function toDatetimeLabel (value) {
  return value
    ? dateService.formatCustom(value, FORMATS.isoDatetime, FORMATS.datetimeShort)
    : null
}

/**
 * The "done out of expected" pairs the design shows as a single cell - the visited
 * control points and, in kilometres, the distance covered out of the distance the
 * route asks for.
 */
export function toRatioLabel (done, total) {
  const left = done ?? 0
  const right = total ?? 0

  // Spaced around the slash: run together, a pair of one-decimal figures reads as one
  // long number rather than as two.
  return `${left} / ${right}`
}

/**
 * Metres as the kilometres the screens show, to one decimal.
 *
 * Whole kilometres were too coarse to read a shift by: a route of a few hundred metres
 * and one of one and a half kilometres both came out as a flat number that said nothing
 * about either.
 */
function toKilometres (metres) {
  const value = metres === null || metres === undefined ? 0 : Number(metres)

  return (value / METRES_IN_KILOMETRE).toFixed(KILOMETRE_DECIMALS)
}

export function toControlPointsLabel (record) {
  return toRatioLabel(record?.locations?.completed, record?.locations?.total)
}

export function toKilometresLabel (record) {
  const coverage = record?.coverage || {}

  return toRatioLabel(
    toKilometres(coverage.covered_metres),
    toKilometres(coverage.required_metres)
  )
}

/**
 * How much of the route is done, as the percentage the backend counts it in. Reported as
 * null for an order whose route asks for nothing - there is no share of nothing.
 *
 * Shown whole. The backend counts it to two decimals, and neither of them changes how a
 * route is read - but the rounding is only ever shown, never counted on: 99.6 % reads as
 * 100 % here while the figure the coverage is invoiced on stays what the backend said.
 */
export function toCoveragePercentLabel (record) {
  const percent = record?.coverage?.percent

  if (percent === null || percent === undefined) {
    return '-'
  }

  return `${Number(percent).toFixed(COVERAGE_PERCENT_DECIMALS)} %`
}

/**
 * `REGULAR` / `INTERVENTION` as the backend reports them, said in the user's language.
 */
export function toKindLabel (translate, kind) {
  if (kind === WORK_ORDER_KIND.intervention) {
    return translate(`${NS}.type_intervention`)
  }

  if (kind === WORK_ORDER_KIND.regular) {
    return translate(`${NS}.type_regular`)
  }

  return ''
}

/**
 * The window an order was driven in, which is how the irregularities reported against
 * its task and its vehicle are narrowed down to the ones that belong to it - the same
 * window the backend counts them in.
 */
/**
 * The task an order runs. The listing reports it on the row itself; the details endpoint
 * reports it under the plan's snapshot - both shapes reach the map, so both are read.
 */
export function toTaskId (record) {
  return record?.task?.id ?? record?.task_data?.task?.id ?? null
}

export function buildIrregularityFilters (record) {
  // The listing reports the pair on the row itself; the details endpoint reports it
  // under the plan's snapshot. Both shapes reach this, so both are read.
  const taskId = record?.task?.id ?? record?.task_data?.task?.id
  const vehicleId = record?.vehicle?.id ?? record?.task_data?.vehicle?.id

  if (!taskId || !vehicleId) {
    return null
  }

  const from = record?.started_at || record?.planned_start
  const to = record?.finished_at

  return {
    task_id: taskId,
    vehicle_id: vehicleId,
    // The listing widens these to whole days itself, so only the date part is sent.
    ...(from ? { date_from: String(from).slice(0, 10) } : {}),
    ...(to ? { date_to: String(to).slice(0, 10) } : {})
  }
}

/**
 * The details record -> the flat keys the "Podaci o zadatku" fields are bound to. The
 * order itself carries the status and the id; everything else is the plan's snapshot
 * of the task, under `task_data`.
 */
export function toTaskDataForm (record) {
  const { task_data: taskData, status } = record || {}
  const { task, vehicle, base, subcontractor, operator } = taskData || {}

  return {
    status: status?.description ?? null,
    task_name: task?.name ?? null,
    task_type: task?.type?.description ?? null,
    task_category: task?.category?.description ?? null,
    surface_type: task?.surface_type?.description ?? null,
    cleaning_type: taskData?.cleaning_type?.description ?? null,
    vehicle_type: vehicle?.type?.description ?? null,
    drive_type: vehicle?.drive_type?.description ?? null,
    registration: vehicle?.registration ?? null,
    driver: taskData?.driver?.full_name ?? null,
    worker_count: task?.worker_count ?? null,
    spread_type: taskData?.spread_type?.description ?? null,
    spread_quantity: taskData?.spread_quantity ?? null,
    throw_side: vehicle?.throw_side?.description ?? null,
    contractor: subcontractor?.name ?? null,
    responsible_base: base?.name ?? null,
    base_responsible_person: base?.responsible_person?.name ?? null,
    operator: operator?.name ?? null
  }
}

/**
 * How the order's streets went, counted: cleared / partially cleared / not cleared. The
 * three add up to the street count, so the total is left out - it is the sum of what is
 * shown, and a fourth figure only invites adding them up to check.
 */
export function toStreetsSummaryLabel (record) {
  const streets = record?.streets

  if (!streets?.total) {
    return null
  }

  return `${streets.covered ?? 0} / ${streets.partial ?? 0} / ${streets.uncovered ?? 0}`
}

/**
 * A span of minutes as "3 h 25 min", or "25 min" under the hour.
 *
 * Hours and minutes rather than a clock reading: "03:25" beside a column of timestamps
 * reads as a time of day, which is exactly what this is not.
 */
export function toDurationLabel (minutes) {
  if (minutes === null || minutes === undefined) {
    return null
  }

  const total = Math.max(0, Math.round(Number(minutes)))
  const hours = Math.floor(total / MINUTES_IN_HOUR)
  const rest = total % MINUTES_IN_HOUR

  return hours ? `${hours} h ${rest} min` : `${rest} min`
}

/**
 * How long the order has been under way: from taking it over to finishing it.
 *
 * An order still being driven is measured to now - a snapshot, as the whole screen is,
 * which the tab's refresh button brings up to date. Nothing to measure from means nothing
 * to show: an order that was never taken over has no duration, not a duration of zero.
 */
export function toElapsedLabel (record) {
  const { started_at: startedAt, finished_at: finishedAt } = record || {}

  if (!startedAt) {
    return null
  }

  return toDurationLabel(dateService.diff(
    startedAt,
    finishedAt || dateService.now(FORMATS.isoDatetime),
    'minute',
    FORMATS.isoDatetime
  ))
}

/**
 * The time actually worked, as the backend sums the work intervals - see the controller's
 * `work()`. An order being driven right now is still counting, which the label says
 * rather than passing a figure off as final.
 */
export function toWorkTimeLabel (translate, record) {
  const { seconds, is_working: isWorking } = record?.work || {}

  if (seconds === null || seconds === undefined) {
    return null
  }

  const label = toDurationLabel(Number(seconds) / SECONDS_IN_MINUTE)

  return isWorking ? `${label} (${translate(`${NS}.work_in_progress`)})` : label
}

/**
 * The details record -> the flat keys the "Podaci o izvršenju" fields are bound to.
 *
 * The four timestamps are deliberately not bound under the names they arrive as
 * (`planned_start`, `started_at`, ...): `applyExecutionForm` writes these keys onto the
 * record itself, and the map's own reading of it - `buildIrregularityFilters` narrows the
 * reports by the window the order was driven in - needs the raw `Y-m-d H:i:s` values
 * still to be there, not the formatted labels.
 */
export function toExecutionForm (translate, record) {
  const execution = record?.execution || {}

  return {
    status: record?.status?.description ?? null,
    coverage_percent: toCoveragePercentLabel(execution),
    kilometres_travelled: toKilometresLabel(execution),
    visited_points: toControlPointsLabel(execution),
    streets_summary: toStreetsSummaryLabel(execution),
    irregularity_count: execution?.irregularity_count ?? 0,
    planned_from: toDatetimeLabel(record?.planned_start),
    planned_to: toDatetimeLabel(record?.planned_end),
    taken_at: toDatetimeLabel(record?.started_at),
    completed_at: toDatetimeLabel(record?.finished_at),
    elapsed: toElapsedLabel(record),
    work_time: toWorkTimeLabel(translate, execution)
  }
}

/**
 * Writes the flat keys onto the record itself. Content.vue hands `onDataReceived` the
 * very object it then turns into the form data, so the flat values have to land on it
 * rather than on a copy.
 */
export function applyTaskDataForm (record) {
  if (!record) {
    return record
  }

  return Object.assign(record, toTaskDataForm(record))
}

export function applyExecutionForm (translate, record) {
  if (!record) {
    return record
  }

  return Object.assign(record, toExecutionForm(translate, record))
}
